import { useCallback, useEffect, useState } from 'react';
import { calculaTotal } from '../index';

const getSortValue = (key, row) => {
  if (key.includes('.')) {
    const [key1, key2] = key.split('.');
    return row[key1]?.[key2];
  }
  return row[key];
};

const compareRowsByKey = (a, b, orderKey) => {
  const va = getSortValue(orderKey, a);
  const vb = getSortValue(orderKey, b);
  if (va === vb) return 0;
  if (typeof va === 'number' && typeof vb === 'number') {
    return va - vb;
  }
  return String(va ?? '').localeCompare(String(vb ?? ''), 'pt-BR', { numeric: true, sensitivity: 'base' });
};

/**
 * Hook para gerenciar filtros, paginação e seleção de colaboradores
 */
export const useApontamentoFilters = (payroll, selectedCollaborators) => {
  const [filterName, setFilterName] = useState(undefined);
  const [pagination, setPagination] = useState({
    itemsPerPage: 8,
    currentPage: 1,
  });
  const [orderBy, setOrderBy] = useState({});
  const [totals, setTotals] = useState({});

  const getFilteredItems = useCallback(() => {
    if (!payroll?.items) return [];
    return payroll.items
      .filter(({ cooperator }) => selectedCollaborators.includes(cooperator.id))
      .map((item) => ({ ...item, requisicao: payroll.id }));
  }, [payroll, selectedCollaborators]);

  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/([\u0300-\u036f]|[^0-9a-zA-Z\s])/g, '');
  };

  const filter = useCallback(() => {
    if (!payroll?.items) {
      return { items: [], total: 0 };
    }

    let filteredCollaborators = getFilteredItems();

    if (filterName) {
      const needle = normalizeText(filterName);
      filteredCollaborators = filteredCollaborators.filter((c) => normalizeText(c?.cooperator?.name).includes(needle));
    }

    const [orderKey] = Object.keys(orderBy || {});
    if (orderKey && orderBy[orderKey]) {
      const sorted = [...filteredCollaborators].sort((a, b) => compareRowsByKey(a, b, orderKey));
      if (orderBy[orderKey] === 'desc') {
        sorted.reverse();
      }
      filteredCollaborators = sorted;
    }

    const total = filteredCollaborators.length;

    const paginatedItems = filteredCollaborators.slice(
      (pagination.currentPage - 1) * pagination.itemsPerPage,
      pagination.currentPage * pagination.itemsPerPage
    );

    return { items: paginatedItems, total };
  }, [payroll, filterName, pagination, getFilteredItems, orderBy]);

  const [filteredCollaboratorsPagination, setFilteredCollaboratorsPagination] = useState(filter());

  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [filterName]);

  useEffect(() => {
    setFilteredCollaboratorsPagination(filter());
  }, [payroll, pagination.itemsPerPage, pagination.currentPage, selectedCollaborators, filter]);

  useEffect(() => {
    const items = getFilteredItems();
    setTotals({
      producao: calculaTotal(items, 'producao'),
      variavel: calculaTotal(items, 'variavel'),
      reembolso: calculaTotal(items, 'reembolso'),
      totalRendimentos: calculaTotal(items, 'totalRendimentos'),
      descontosDiversos: calculaTotal(items, 'descontosDiversos'),
      adiantamento: calculaTotal(items, 'adiantamento'),
      totalDebitos: calculaTotal(items, 'totalDebitos'),
      liquido: calculaTotal(items, 'liquido'),
    });
  }, [selectedCollaborators, payroll, getFilteredItems]);

  const goToFirstPage = useCallback(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePagination = useCallback((page, pageSize) => {
    setPagination((prev) => ({ ...prev, currentPage: page, itemsPerPage: pageSize }));
  }, []);

  const changePaginationSize = useCallback((page, pageSize) => {
    setPagination((prev) => ({ ...prev, currentPage: 1, itemsPerPage: pageSize }));
  }, []);

  return {
    filterName,
    setFilterName,
    pagination,
    goToFirstPage,
    changePagination,
    changePaginationSize,
    filteredCollaboratorsPagination,
    orderBy,
    setOrderBy,
    totals,
  };
};
